import type { StorefrontFeatureKey } from "@loot-os/api-contract";
import {
  type AsyncRouteComponent,
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
} from "@tanstack/react-router";

import { App } from "./App.js";
import { FeatureGate } from "./components/FeatureGate.js";
import { NotFound } from "./components/NotFound.js";
import { RouteError } from "./components/RouteError.js";
import { parseAccountSearch } from "./templates/pages/account/content/search.js";
import { parseBoxesSearch } from "./templates/pages/boxes/content/search.js";
import { parseBoxListSearch } from "./templates/pages/shared/boxListSearch.js";

// Every page is its own chunk: the shell (App, the frame, the providers) loads with the document,
// and a page's code with its route. The router loads a route's chunk before it renders the route
// (keeping the page the shopper is on until then), and a link's on hover or touch.
const AccountPage = lazyRouteComponent(() => import("./pages/AccountPage.js"), "AccountPage");
const BattleCreatePage = lazyRouteComponent(
  () => import("./pages/BattleCreatePage.js"),
  "BattleCreatePage",
);
const BattlePage = lazyRouteComponent(() => import("./pages/BattlePage.js"), "BattlePage");
const BattlesPage = lazyRouteComponent(() => import("./pages/BattlesPage.js"), "BattlesPage");
const BoxDetailPage = lazyRouteComponent(() => import("./pages/BoxDetailPage.js"), "BoxDetailPage");
const BoxesPage = lazyRouteComponent(() => import("./pages/BoxesPage.js"), "BoxesPage");
const FairnessPage = lazyRouteComponent(() => import("./pages/FairnessPage.js"), "FairnessPage");
const ForgeCreatePage = lazyRouteComponent(
  () => import("./pages/ForgeCreatePage.js"),
  "ForgeCreatePage",
);
const ForgeEditPage = lazyRouteComponent(() => import("./pages/ForgeEditPage.js"), "ForgeEditPage");
const ForgeMinePage = lazyRouteComponent(() => import("./pages/ForgeMinePage.js"), "ForgeMinePage");
const ForgePage = lazyRouteComponent(() => import("./pages/ForgePage.js"), "ForgePage");
const HomePage = lazyRouteComponent(() => import("./pages/HomePage.js"), "HomePage");
const InventoryPage = lazyRouteComponent(() => import("./pages/InventoryPage.js"), "InventoryPage");
const LiveBreakHistoryDetailPage = lazyRouteComponent(
  () => import("./pages/LiveBreakHistoryDetailPage.js"),
  "LiveBreakHistoryDetailPage",
);
const LiveBreakHistoryPage = lazyRouteComponent(
  () => import("./pages/LiveBreakHistoryPage.js"),
  "LiveBreakHistoryPage",
);
const LiveBreakHostPage = lazyRouteComponent(
  () => import("./pages/LiveBreakHostPage.js"),
  "LiveBreakHostPage",
);
const LiveBreakPage = lazyRouteComponent(() => import("./pages/LiveBreakPage.js"), "LiveBreakPage");
const LiveBreaksPage = lazyRouteComponent(
  () => import("./pages/LiveBreaksPage.js"),
  "LiveBreaksPage",
);
const LoginConfirmPage = lazyRouteComponent(
  () => import("./pages/LoginConfirmPage.js"),
  "LoginConfirmPage",
);
const LoginPage = lazyRouteComponent(() => import("./pages/LoginPage.js"), "LoginPage");
const PrivacyPage = lazyRouteComponent(() => import("./pages/PrivacyPage.js"), "PrivacyPage");
const TermsPage = lazyRouteComponent(() => import("./pages/TermsPage.js"), "TermsPage");

/**
 * A page behind the tenant's switch for one of its features (a 404 with it off), keeping the
 * page's `preload`, so the router still loads its chunk with the route rather than suspending
 * inside the gate.
 */
function gated<P extends object>(
  feature: StorefrontFeatureKey,
  Page: AsyncRouteComponent<P>,
  props: P = {} as P,
) {
  const GatedPage = () => (
    <FeatureGate feature={feature}>
      <Page {...props} />
    </FeatureGate>
  );
  GatedPage.preload = Page.preload;
  return GatedPage;
}

const rootRoute = createRootRoute({
  component: App,
  notFoundComponent: NotFound,
});

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: HomePage,
});

const boxesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/boxes",
  component: BoxesPage,
  // The tab and the filters, so a filtered grid survives opening a box and coming back.
  validateSearch: parseBoxesSearch,
});

const boxDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/boxes/$boxId",
  component: BoxDetailPage,
});

const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account",
  component: AccountPage,
  // The tab and settings panel showing, and Stripe's checkout return (?purchase=…&session_id=…).
  validateSearch: parseAccountSearch,
});

const inventoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inventory",
  component: InventoryPage,
});

const fairnessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/fairness",
  component: FairnessPage,
  // ?openId=<id> auto-verifies that open; the account page's history links here.
  validateSearch: (search: Record<string, unknown>): { openId?: string } => ({
    openId: typeof search.openId === "string" && search.openId ? search.openId : undefined,
  }),
});

// Battles: every route behind the tenant's switch (docs/battles.md).
const battlesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/battles",
  component: gated("battles", BattlesPage),
});

// The shopper's own battles: the lobby's "mine" view, which the arcade layout draws as a page of
// its own under the lobby.
const battleHistoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/battles/history",
  component: gated("battles", BattlesPage, { view: "mine" }),
});

const battleCreateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/battles/create",
  component: gated("battles", BattleCreatePage),
  // ?boxId=<id> starts the picks with one round of that box; the box page's Battle link sets it.
  validateSearch: (search: Record<string, unknown>): { boxId?: string } => ({
    boxId: typeof search.boxId === "string" && search.boxId ? search.boxId : undefined,
  }),
});

const battleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/battles/$battleId",
  component: gated("battles", BattlePage),
  // ?replay=1 re-runs a completed battle's choreography once.
  // The router parses `1` as a number, so accept either form.
  validateSearch: (search: Record<string, unknown>): { replay?: string } => ({
    replay:
      (typeof search.replay === "string" || typeof search.replay === "number") && search.replay
        ? String(search.replay)
        : undefined,
  }),
});

// The forge: every route behind the tenant's switch (docs/forge.md).
const forgeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forge",
  component: gated("forge", ForgePage),
  // The filters, so a filtered grid survives opening a box and coming back.
  validateSearch: parseBoxListSearch,
});

const forgeCreateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forge/create",
  component: gated("forge", ForgeCreatePage),
});

const forgeMineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forge/mine",
  component: gated("forge", ForgeMinePage),
});

const forgeEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forge/$boxId/edit",
  component: gated("forge", ForgeEditPage),
});

// Live breaks: every route behind the tenant's switch (docs/live-breaks.md, "Storefront").
const liveBreaksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/live-breaks",
  component: gated("liveBreaks", LiveBreaksPage),
});

// The host's pages (M6), ahead of the live break's own route, whose `$liveBreakId` they would
// otherwise read as an id: the dashboard (any signed-in shopper, host or not, so one who left the
// Host Allow List still claims their commission and manages their Blocks), what they hosted, and
// one hosted live break's stats.
const liveBreakHostRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/live-breaks/host",
  component: gated("liveBreaks", LiveBreakHostPage),
});

const liveBreakHistoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/live-breaks/host/history",
  component: gated("liveBreaks", LiveBreakHistoryPage),
});

const liveBreakHistoryDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/live-breaks/host/history/$liveBreakId",
  component: gated("liveBreaks", LiveBreakHistoryDetailPage),
});

const liveBreakRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/live-breaks/$liveBreakId",
  component: gated("liveBreaks", LiveBreakPage),
  // ?twitchLink=ok|<error>: a Twitch link that came back here with no popup to answer it
  // (`TWITCH_LINK_RESULT_PARAM`); the page reads it once and drops it.
  validateSearch: (search: Record<string, unknown>): { twitchLink?: string } => ({
    twitchLink:
      typeof search.twitchLink === "string" && search.twitchLink ? search.twitchLink : undefined,
  }),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
  // ?error=invalid_link comes from the BFF's magic-link callback (apps/storefront/server/app.ts).
  validateSearch: (search: Record<string, unknown>): { error?: string } => ({
    error: typeof search.error === "string" && search.error ? search.error : undefined,
  }),
});

// A sign-in link opened in a browser that did not ask for it (apps/storefront/server/app.ts).
const loginConfirmRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login/confirm",
  component: LoginConfirmPage,
});

// The policy pages: no feature gate, because a store cannot be without its terms.
const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/terms",
  component: TermsPage,
});

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/privacy",
  component: PrivacyPage,
});

const routeTree = rootRoute.addChildren([
  homeRoute,
  boxesRoute,
  boxDetailRoute,
  battlesRoute,
  battleHistoryRoute,
  battleCreateRoute,
  battleRoute,
  forgeRoute,
  forgeCreateRoute,
  forgeMineRoute,
  forgeEditRoute,
  liveBreaksRoute,
  liveBreakHostRoute,
  liveBreakHistoryRoute,
  liveBreakHistoryDetailRoute,
  liveBreakRoute,
  accountRoute,
  inventoryRoute,
  fairnessRoute,
  loginRoute,
  loginConfirmRoute,
  termsRoute,
  privacyRoute,
]);

/** The storefront's route tree; mounted by main.tsx through RouterProvider. */
export const router = createRouter({
  routeTree,
  defaultErrorComponent: RouteError,
  // A link's page starts loading on hover or touch, so it is usually there by the click.
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
